/**
 * The one fold of the Results page (DESIGN-RESULTS §9): a row with a chevron, a name, one summary sentence and an action word; open, the content sits under the row and the word reads "Hide".
 * Each fold lives at the foot of the card it explains. Controlled with `open`/`onOpenChange` or on its own.
 */
import { useState, type ReactNode } from "react";

export function Fold({ name, summary, children, testId, open: controlled, onOpenChange, defaultOpen = false, words = ["Show", "Hide"], keepMounted = false }: {
  readonly name: string; readonly summary: ReactNode; readonly children: ReactNode; readonly testId: string;
  readonly open?: boolean; readonly onOpenChange?: (open: boolean) => void; readonly defaultOpen?: boolean;
  /** The words on the row's right: default "Show" and "Hide". */ readonly words?: readonly [open: string, hide: string];
  /** Closed, the content stays in the page but hidden (a fold over a table that other parts of the page quote). */ readonly keepMounted?: boolean;
}) {
  const [own, setOwn] = useState(defaultOpen);
  const open = controlled ?? own;
  const toggle = (): void => { setOwn(!open); onOpenChange?.(!open); };
  return (
    <div className="gx-fold" data-open={open} data-testid={`${testId}.fold`}>
      <button type="button" className="gx-fold-row" aria-expanded={open} data-testid={testId} onClick={toggle}>
        <span className="gx-fold-chev" aria-hidden="true">{open ? "▾" : "▸"}</span>
        <b className="gx-fold-name">{name}</b>
        <span className="gx-fold-sum">{summary}</span>
        <span className="gx-fold-act">{open ? words[1] : words[0]}</span>
      </button>
      {children !== null && (open || keepMounted) ? <div className="gx-fold-body" data-testid={`${testId}.body`} hidden={!open}>{children}</div> : null}
    </div>
  );
}

/** The tag every look-through or illustrative figure carries once (DESIGN-RESULTS §8): a dashed pill, never a solid chip. */
export function DashedTag({ children, testId }: { readonly children: ReactNode; readonly testId?: string }) {
  return <span className="gx-dashtag" data-testid={testId}>{children}</span>;
}

/** The key strip under a chart or table: an info ground, a tag and one sentence, said once per card. */
export function KeyStrip({ tag, children, testId }: { readonly tag?: string | null; readonly children: ReactNode; readonly testId?: string }) {
  return <p className="gx-keystrip" data-testid={testId}>{tag === undefined || tag === null ? null : <DashedTag>{tag}</DashedTag>}<span>{children}</span></p>;
}
